import { useLayoutEffect, useMemo, useRef, type MutableRefObject } from 'react';
import { debounce } from 'lodash-es';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/hooks/useAppSelector';

import { LIST_OFFSET_DEBOUNCE, type SecretListId } from '../constants';
import { setSecretsListOffset } from '../services/actions';
import { selectSecretsListOffset } from '../services/selectors';

export function useSecretsListOffset(
    listId: SecretListId,
    listRef: MutableRefObject<{
        scrollTo: (offset: number) => void;
    } | null>,
) {
    const dispatch = useDispatch();

    const offset = useAppSelector(selectSecretsListOffset);

    const handleOffset = useMemo(
        () =>
            debounce((offset: number) => {
                dispatch(setSecretsListOffset(offset, listId));
            }, LIST_OFFSET_DEBOUNCE),
        [dispatch, listId],
    );

    const offsetRef = useRef(offset);

    useLayoutEffect(() => {
        const isOffsetOfGivenList = offsetRef.current?.listId === listId;

        listRef.current?.scrollTo(isOffsetOfGivenList ? (offsetRef.current?.offset ?? 0) : 0);
    }, [listId, listRef, dispatch]);

    return handleOffset;
}
